import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface QuestGiftBoxProps {
  workerId: string;
}

export function QuestGiftBox({ workerId }: QuestGiftBoxProps) {
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    fetchPendingQuests();
    
    // Subscribe to realtime updates
    const channel = supabase
      .channel('worker_quests_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'worker_quests',
          filter: `worker_id=eq.${workerId}`,
        },
        () => {
          fetchPendingQuests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [workerId]);

  const fetchPendingQuests = async () => {
    const { count, error } = await supabase
      .from('worker_quests')
      .select('*', { count: 'exact', head: true })
      .eq('worker_id', workerId)
      .in('status', ['pending', 'in_progress']);

    if (!error && count !== null) {
      setPendingCount(count);
      setIsVisible(count > 0);
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        className="fixed bottom-24 right-4 z-50"
      >
        <motion.button
          onClick={() => navigate('/quests')}
          className="relative p-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-orange-500/30"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          animate={{
            y: [0, -8, 0],
          }}
          transition={{
            y: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            },
          }}
        >
          <Gift className="h-7 w-7 text-white" />
          
          {/* Pulse effect */}
          <motion.div
            className="absolute inset-0 rounded-full bg-amber-400"
            animate={{
              scale: [1, 1.5, 1.5],
              opacity: [0.5, 0, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeOut",
            }}
          />
          
          {/* Badge count */}
          {pendingCount > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5 rounded-full bg-red-500 text-white text-sm font-bold flex items-center justify-center shadow-md"
            >
              {pendingCount}
            </motion.div>
          )}

          {/* Sparkles */}
          <motion.div
            className="absolute -top-2 -left-2 text-yellow-300"
            animate={{
              opacity: [0, 1, 0],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: 0,
            }}
          >
            ✨
          </motion.div>
          <motion.div
            className="absolute -bottom-1 -right-2 text-yellow-300"
            animate={{
              opacity: [0, 1, 0],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: 0.5,
            }}
          >
            ✨
          </motion.div>
        </motion.button>
      </motion.div>
    </AnimatePresence>
  );
}
